import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Check, Loader2, FolderPlus, FolderOpen } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import './list-selection-modal.css';

interface UserList {
  list_id: string;
  name: string;
  description?: string;
  contact_count: number;
  tags?: string[];
  is_default?: boolean;
  created_at: string;
  updated_at: string;
}

interface ListSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (listIds: string[], selectAll: boolean, shouldRefreshLists: boolean) => void;
  selectedCount: number;
  selectAll: boolean;
  totalCount: number;
}

const ListSelectionModal: React.FC<ListSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  selectAll,
  totalCount
}) => {
  const { showToast, state } = useAppContext();
  const { getLists, createList } = useApi();
  
  const [lists, setLists] = useState<UserList[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [shouldRefreshLists, setShouldRefreshLists] = useState(false);

  // Simple count display
  const displayCount = selectAll ? totalCount : selectedCount;

  // Load lists - simplified without data source restrictions
  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLists(true) as { success: boolean; lists?: UserList[] };
      if (data?.success) {
        setLists(data.lists || []);
      } else {
        throw new Error('Failed to load lists');
      }
    } catch (error) {
      console.error('Failed to load lists:', error);
      setLists([]);
      showToast({
        type: 'error',
        title: 'Error Loading Lists',
        message: 'Failed to load contact lists'
      });
    } finally {
      setLoading(false);
    }
  }, [getLists, showToast]);

  // Load lists when modal opens
  useEffect(() => {
    if (isOpen) {

      loadLists();
      // Reset form state
      setSelectedListIds(new Set());
      setShowCreateForm(false);
      setNewListName('');
      setShouldRefreshLists(false);
    }
  }, [isOpen, loadLists]);

  // Handle list selection
  const toggleListSelection = useCallback((listId: string) => {
    setSelectedListIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  }, []);

  // Create new list
  const handleCreateList = useCallback(async () => {
    if (!newListName.trim()) {
      showToast({
        type: 'warning',
        title: 'List Name Required',
        message: 'Please enter a name for your new list'
      });
      return;
    }

    setCreatingList(true);
    try {
      const data = await createList(
        newListName.trim(),
        undefined,
        [],
        false
      ) as { success: boolean; list?: UserList };
      
      if (data?.success && data.list) {
        setLists(prev => [data.list!, ...prev]);
        setSelectedListIds(prev => new Set([...prev, data.list!.list_id]));
        setShouldRefreshLists(true);
        
        showToast({
          type: 'success',
          title: 'List Created',
          message: `Successfully created "${data.list.name}"`
        });
        
        setNewListName('');
        setShowCreateForm(false);
      } else {
        throw new Error('Failed to create list');
      }
    } catch {
      showToast({
        type: 'error',
        title: 'Error Creating List',
        message: 'Failed to create list'
      });
    } finally {
      setCreatingList(false);
    }
  }, [newListName, createList, showToast]);

  // Handle confirmation
  const handleConfirm = useCallback(() => {
    if (selectedListIds.size === 0) {
      showToast({
        type: 'warning',
        title: 'No Lists Selected',
        message: 'Please select at least one list or create a new one'
      });
      return;
    }
    onConfirm(Array.from(selectedListIds), selectAll, shouldRefreshLists);
    onClose();
  }, [selectedListIds, selectAll, shouldRefreshLists, onConfirm, onClose, showToast]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content list-selection-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{state.searchState.dataSource === 'people' ? 'Add Contacts' : 'Add to List'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="selection-summary">
            {state.searchState.dataSource === 'people' ? (
              <span>Unlocking {displayCount.toLocaleString()} contact{displayCount === 1 ? '' : 's'}:</span>
            ) : (
              <span>Adding {displayCount.toLocaleString()} contact{displayCount !== 1 ? 's' : ''} to:</span>
            )}
          </div>

          {/* Create New List Section */}
          <div className="create-list-section">
            <button
              className={`create-list-toggle ${showCreateForm ? 'active' : ''}`}
              onClick={() => setShowCreateForm(!showCreateForm)}
              disabled={creatingList}
            >
              <FolderPlus size={16} />
              <span>Create New List</span>
            </button>

            {showCreateForm && (
              <div className="create-list-form">
                <div className="form-group">
                  <label htmlFor="new-list-name">List Name *</label>
                  <input
                    id="new-list-name"
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="Enter list name..."
                    maxLength={200}
                    disabled={creatingList}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="button-primary"
                    onClick={handleCreateList}
                    disabled={creatingList || !newListName.trim()}
                  >
                    {creatingList ? (
                      <>
                        <Loader2 size={16} className="spinning" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Create List
                      </>
                    )}
                  </button>
                  <button
                    className="button-secondary"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewListName('');
                    }}
                    disabled={creatingList}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Existing Lists Section */}
          <div className="existing-lists-section">
            <h3>Existing Lists</h3>
            
            {loading ? (
              <div className="loading-state">
                <Loader2 size={20} className="spinning" />
                <span>Loading lists...</span>
              </div>
            ) : lists.length === 0 ? (
              <div className="empty-state">
                <FolderOpen size={48} />
                <p>No lists found</p>
                <p>Create your first list to get started</p>
              </div>
            ) : (
              <div className="lists-grid">
                {lists.map(list => (
                    <div
                      key={list.list_id}
                      className={`list-item ${selectedListIds.has(list.list_id) ? 'selected' : ''}`}
                      onClick={() => toggleListSelection(list.list_id)}
                    >
                      <div className="list-item-header">
                        <div className="list-item-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedListIds.has(list.list_id)}
                            onChange={() => toggleListSelection(list.list_id)}
                          />
                          <div className="checkbox-visual">
                            {selectedListIds.has(list.list_id) && <Check size={10} />}
                          </div>
                        </div>
                        <div className="list-item-info">
                          <h4 className="list-name">
                            {list.name}
                            {list.is_default && <span className="default-badge">Default</span>}
                          </h4>
                          <div className="list-meta">
                            <span className="contact-count">
                              {(list.contact_count ?? 0).toLocaleString()} contact{(list.contact_count ?? 0) !== 1 ? 's' : ''}
                            </span>
                            {list.tags && list.tags.length > 0 && (
                              <div className="list-tags">
                                {list.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="tag">{tag}</span>
                                ))}
                                {list.tags.length > 3 && (
                                  <span className="tag-more">+{list.tags.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="selection-info">
            {selectedListIds.size > 0 && (
              <span>{selectedListIds.size} list{selectedListIds.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="modal-actions">
            <button className="button-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button-primary"
              onClick={handleConfirm}
              disabled={selectedListIds.size === 0}
            >
              {state.searchState.dataSource === 'people' 
                ? `Unlock${selectedListIds.size > 1 ? ` & Add to ${selectedListIds.size} Lists` : ' Contacts'}`
                : `Add to List${selectedListIds.size !== 1 ? 's' : ''}`
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListSelectionModal;